import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider } from "./lib/auth";
import { ConsentProvider } from "./lib/consent";
import { ThemeProvider } from "./lib/theme";
import { installGlobalErrorTracking } from "./lib/analytics/errorTracking";
import { RouteTracker } from "./lib/analytics/RouteTracker";
import { ConsentBanner } from "./components/ConsentBanner";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { ToastProvider } from "./components/ui";
import { App } from "./App";
import "./styles.css";

// Before anything mounts, so a crash during initial render is still caught.
installGlobalErrorTracking();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Football data moves slowly; a short stale window keeps the app snappy without
      // hammering the API on every navigation.
      staleTime: 30_000,
      retry: (failureCount, error) => {
        const status = (error as { status?: number }).status;
        if (status && status >= 400 && status < 500) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: true,
    },
  },
});

/** Deliberately minimal — this is what a visitor sees if literally everything else broke, so
 *  it must not depend on anything that could itself be the thing that broke. */
function CrashScreen() {
  return (
    <div className="grid min-h-dvh place-items-center p-6 text-center">
      <div>
        <p className="mb-2 text-lg font-semibold text-fg">Something went wrong.</p>
        <p className="mb-5 text-sm text-fg-subtle">Reloading usually fixes it.</p>
        <button
          onClick={() => window.location.reload()}
          className="rounded-xl bg-accent px-4 py-2.5 text-sm font-semibold text-accent-fg"
        >
          Reload
        </button>
      </div>
    </div>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ErrorBoundary name="root" fallback={<CrashScreen />}>
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <ConsentProvider>
              <ToastProvider>
                {/* Outside <AuthProvider> so a route change is tracked no matter the auth
                    state — signed out, choosing a username, or fully signed in. */}
                <RouteTracker />
                <AuthProvider>
                  <App />
                </AuthProvider>
                <ConsentBanner />
              </ToastProvider>
            </ConsentProvider>
          </BrowserRouter>
        </QueryClientProvider>
      </ThemeProvider>
    </ErrorBoundary>
  </StrictMode>,
);
